import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import apiAxios from 'helpers/apiAxios';
import { handleAsyncThunkError } from 'redux/reduxHelpers';

const BASE = '/crm/dashboard/sales';

export const fetchSalesSummary = createAsyncThunk(
  'dashboardSales/fetchSummary',
  async (params, { rejectWithValue }) => {
    try {
      const res = await apiAxios.get(`${BASE}/get_summary_data`, { params });
      return res.data.data;
    } catch (e) {
      return rejectWithValue(handleAsyncThunkError(e, 'Failed to fetch sales summary'));
    }
  }
);

export const fetchSalesPipeline = createAsyncThunk(
  'dashboardSales/fetchPipeline',
  async (params, { rejectWithValue }) => {
    try {
      const res = await apiAxios.get(`${BASE}/get_pipeline_data`, { params });
      return res.data.data;
    } catch (e) {
      return rejectWithValue(handleAsyncThunkError(e, 'Failed to fetch pipeline data'));
    }
  }
);

export const fetchSalesQuotation = createAsyncThunk(
  'dashboardSales/fetchQuotation',
  async (params, { rejectWithValue }) => {
    try {
      const res = await apiAxios.get(`${BASE}/get_quotation_data`, { params });
      return res.data.data;
    } catch (e) {
      return rejectWithValue(handleAsyncThunkError(e, 'Failed to fetch quotation stages'));
    }
  }
);

export const fetchSalesConversion = createAsyncThunk(
  'dashboardSales/fetchConversion',
  async (params, { rejectWithValue }) => {
    try {
      const res = await apiAxios.get(`${BASE}/get_conversion_data`, { params });
      return res.data.data;
    } catch (e) {
      return rejectWithValue(handleAsyncThunkError(e, 'Failed to fetch conversion funnel'));
    }
  }
);

export const fetchSalesTopContracts = createAsyncThunk(
  'dashboardSales/fetchTopContracts',
  async (params, { rejectWithValue }) => {
    try {
      const res = await apiAxios.get(`${BASE}/get_top_contracts`, { params });
      return res.data.data;
    } catch (e) {
      return rejectWithValue(handleAsyncThunkError(e, 'Failed to fetch top contracts'));
    }
  }
);

export const fetchSalesLost = createAsyncThunk(
  'dashboardSales/fetchLost',
  async (params, { rejectWithValue }) => {
    try {
      const res = await apiAxios.get(`${BASE}/get_lost_data`, { params });
      return res.data.data;
    } catch (e) {
      return rejectWithValue(handleAsyncThunkError(e, 'Failed to fetch lost projects'));
    }
  }
);

export const fetchSalesSalesUnit = createAsyncThunk(
  'dashboardSales/fetchSalesUnit',
  async (params, { rejectWithValue }) => {
    try {
      const res = await apiAxios.get(`${BASE}/get_sales_unit_data`, { params });
      return res.data.data;
    } catch (e) {
      return rejectWithValue(handleAsyncThunkError(e, 'Failed to fetch sales unit stats'));
    }
  }
);

export const fetchSalesTrend = createAsyncThunk(
  'dashboardSales/fetchTrend',
  async (params, { rejectWithValue }) => {
    try {
      const res = await apiAxios.get(`${BASE}/get_trend_data`, { params });
      return res.data.data;
    } catch (e) {
      return rejectWithValue(handleAsyncThunkError(e, 'Failed to fetch trend data'));
    }
  }
);

export const fetchSalesDetailed = createAsyncThunk(
  'dashboardSales/fetchDetailed',
  async (params, { rejectWithValue }) => {
    try {
      const res = await apiAxios.get(`${BASE}/get_detailed_data`, { params });
      return res.data;
    } catch (e) {
      return rejectWithValue(handleAsyncThunkError(e, 'Failed to fetch detailed records'));
    }
  }
);

export const fetchSalesLeadTrend = createAsyncThunk(
  'dashboardSales/fetchLeadTrend',
  async (params, { rejectWithValue }) => {
    try {
      const res = await apiAxios.get(`${BASE}/get_lead_trend_data`, { params });
      return res.data.data;
    } catch (e) {
      return rejectWithValue(handleAsyncThunkError(e, 'Failed to fetch lead trend data'));
    }
  }
);

export const fetchSalesUpcomingShipments = createAsyncThunk(
  'dashboardSales/fetchUpcomingShipments',
  async (params, { rejectWithValue }) => {
    try {
      const res = await apiAxios.get(`${BASE}/get_upcoming_shipments`, { params });
      return res.data.data;
    } catch (e) {
      return rejectWithValue(handleAsyncThunkError(e, 'Failed to fetch upcoming shipments'));
    }
  }
);

export const fetchSalesCompletion = createAsyncThunk(
  'dashboardSales/fetchCompletion',
  async (params, { rejectWithValue }) => {
    try {
      const res = await apiAxios.get(`${BASE}/get_completion_data`, { params });
      return res.data.data;
    } catch (e) {
      return rejectWithValue(handleAsyncThunkError(e, 'Failed to fetch completion data'));
    }
  }
);

export const fetchSalesFilterOptions = createAsyncThunk(
  'dashboardSales/fetchFilterOptions',
  async (params, { rejectWithValue }) => {
    try {
      const res = await apiAxios.get(`${BASE}/filter_options`, { params });
      return res.data.data;
    } catch (e) {
      return rejectWithValue(handleAsyncThunkError(e, 'Failed to fetch filter options'));
    }
  }
);

export const fetchSalesWaivers = createAsyncThunk(
  'dashboardSales/fetchWaivers',
  async (params, { rejectWithValue }) => {
    try {
      const res = await apiAxios.get(`${BASE}/get_waiver_data`, { params });
      return res.data.data;
    } catch (e) {
      return rejectWithValue(handleAsyncThunkError(e, 'Failed to fetch waiver data'));
    }
  }
);

// ── Initial state ──────────────────────────────────────────────────────────

const makeSection = (arrayDefault = false) => ({
  data:    arrayDefault ? [] : null,
  loading: false,
  error:   null
});

const initialState = {
  summary:       makeSection(),
  pipeline:      makeSection(true),
  quotation:     makeSection(true),
  conversion:    makeSection(true),
  topContracts:  makeSection(),
  lost:          makeSection(),
  salesUnit:     makeSection(true),
  trend:         makeSection(true),
  detailed: {
    data:       [],
    pagination: { current_page: 1, per_page: 50, total_records: 0, total_pages: 0 },
    loading:    false,
    error:      null
  },
  filterOptions:      makeSection(),
  leadTrend:          makeSection(true),
  upcomingShipments:  makeSection(),
  completion:         makeSection(),
  waivers:            makeSection(),
  lastUpdated:        null
};

// ── Slice ──────────────────────────────────────────────────────────────────

const thunkMap = [
  ['summary',            fetchSalesSummary],
  ['pipeline',           fetchSalesPipeline],
  ['quotation',          fetchSalesQuotation],
  ['conversion',         fetchSalesConversion],
  ['topContracts',       fetchSalesTopContracts],
  ['lost',               fetchSalesLost],
  ['salesUnit',          fetchSalesSalesUnit],
  ['trend',              fetchSalesTrend],
  ['leadTrend',          fetchSalesLeadTrend],
  ['upcomingShipments',  fetchSalesUpcomingShipments],
  ['completion',         fetchSalesCompletion],
  ['filterOptions',      fetchSalesFilterOptions],
  ['waivers',            fetchSalesWaivers],
];

const dashboardSalesSlice = createSlice({
  name: 'dashboardSales',
  initialState,
  reducers: {
    resetSalesDashboard: () => initialState,
    resetError: (state) => {
      Object.keys(state).forEach((k) => {
        if (state[k] && typeof state[k] === 'object' && 'error' in state[k]) {
          state[k].error = null;
        }
      });
    }
  },
  extraReducers: (builder) => {
    thunkMap.forEach(([key, thunk]) => {
      builder
        .addCase(thunk.pending, (state) => {
          state[key].loading = true;
          state[key].error   = null;
        })
        .addCase(thunk.fulfilled, (state, action) => {
          state[key].data    = action.payload;
          state[key].loading = false;
          state.lastUpdated  = new Date().toISOString();
        })
        .addCase(thunk.rejected, (state, action) => {
          state[key].loading = false;
          state[key].error   = action.payload;
        });
    });

    // Detailed records — has separate pagination field
    builder
      .addCase(fetchSalesDetailed.pending, (state) => {
        state.detailed.loading = true;
        state.detailed.error   = null;
      })
      .addCase(fetchSalesDetailed.fulfilled, (state, action) => {
        state.detailed.data       = action.payload.data || [];
        state.detailed.pagination = action.payload.pagination || initialState.detailed.pagination;
        state.detailed.loading    = false;
        state.lastUpdated         = new Date().toISOString();
      })
      .addCase(fetchSalesDetailed.rejected, (state, action) => {
        state.detailed.loading = false;
        state.detailed.error   = action.payload;
      });
  }
});

export const { resetSalesDashboard, resetError } = dashboardSalesSlice.actions;
export default dashboardSalesSlice.reducer;
